/**
 * Album Session tracklist (AUTO-101).
 *
 * The narrative block used to be pasted whole into every track's prompt as "the whole album's narrator + arc", so a
 * brief written as a numbered list of songs (one idea per line) had no way to say "track 4 is item 4": every track
 * drew on the same few items and the running throughline then pulled later tracks back onto them (2026-11-02: five
 * of six tracks were the same man counting the wars he did not start). When the block is a numbered list, track N is
 * made from item N and only from item N.
 */

export interface TracklistItem {
  n: number;
  text: string;
}

export interface Tracklist {
  /** Anything written before item 1 (album-level notes). */
  preamble: string;
  items: TracklistItem[];
}

// "1. text", "2) text", "- 3. text". Numbering must run 1, 2, 3 ... in order, so a stray "1984. ..." inside an item
// is just more text of that item.
const ITEM_RE = /^\s*(?:[-*•]\s*)?(\d{1,2})\s*[.)]\s+(\S.*)$/;

/** A numbered list of at least two items, or null (then the block is used as before, whole). */
export function parseTracklist(narrative: string | null | undefined): Tracklist | null {
  const text = (narrative ?? '').replace(/\r\n?/g, '\n');
  if (!text.trim()) return null;

  const preamble: string[] = [];
  const raw: string[][] = [];
  for (const line of text.split('\n')) {
    const m = ITEM_RE.exec(line);
    if (m && Number(m[1]) === raw.length + 1) {
      raw.push([m[2]]);
    } else if (raw.length === 0) {
      preamble.push(line);
    } else {
      raw[raw.length - 1].push(line);
    }
  }
  if (raw.length < 2) return null;

  const clean = (lines: string[]): string => lines.join('\n').replace(/\*\*/g, '').replace(/\n{3,}/g, '\n\n').trim();
  return {
    preamble: clean(preamble),
    items: raw.map((lines, i) => ({ n: i + 1, text: clean(lines) })),
  };
}

/**
 * How many tracks the session makes. With a tracklist it never goes past the number of items (the track count is
 * easy to forget to change when the brief is submitted); a smaller target makes just the first items.
 */
export function effectiveTarget(targetCount: number, tracklist: Tracklist | null): number {
  if (!tracklist) return targetCount;
  return Math.max(1, Math.min(targetCount, tracklist.items.length));
}

/** The item for this position, or null when the list has no such item. */
export function itemForPosition(tracklist: Tracklist | null, position: number): TracklistItem | null {
  return tracklist?.items.find(i => i.n === position) ?? null;
}

/**
 * The prompt section that replaces the old "NARRATIVE BLUEPRINT (the whole album's narrator + arc)" block. Carries
 * this track's item only; the other items' text is deliberately not included.
 */
export function buildTracklistBlock(tracklist: Tracklist, item: TracklistItem): string {
  const total = tracklist.items.length;
  const lines = [
    `ALBUM BRIEF — a numbered tracklist of ${total} songs. Every track has its OWN subject; this one is item ${item.n} of ${total}.`,
  ];
  if (tracklist.preamble) {
    lines.push('', `ALBUM-LEVEL NOTES (they apply to the album's voice and feel, not to this song's subject):`, tracklist.preamble);
  }
  lines.push(
    '',
    `THIS TRACK'S ASSIGNMENT — tracklist item ${item.n} of ${total}. Write THIS song from it:`,
    item.text,
    '',
    `Do not write any other tracklist item in this song: they are separate songs on this album. Do not reuse the scene, setting, images, characters or opening lines of the earlier tracks listed below. If the item is a quote or a short idea, use it as the song's central line or hook.`,
  );
  return lines.join('\n');
}

export const TRACKLIST_TITLE_LINE =
  'TITLE & SUBJECT: The "title" field is a placeholder — if the assigned tracklist item names the song (a short title, or a title followed by a dash and a description), keep that title in Block 4; otherwise INVENT a fresh, original title. It should feel like it belongs on this album but stand on its own. Do NOT use the preset name or the album title as the song title.';

export function tracklistContinuityHeader(count: number): string {
  return `ALBUM CONTINUITY (the last ${count} track${count > 1 ? 's' : ''}, for voice and sonic consistency ONLY — each track has its OWN subject. Do NOT reuse their scene, setting, images, characters or opening lines):`;
}
